import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Scale, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <FileText className="w-4 h-4" />
            Legal
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Scale className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Terms of Service</h1>
              <p className="text-slate-500">Last updated: {currentDate}</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm m-0">
                  By using Decoded, you agree to these terms. Please read them carefully before using our services.
                </p>
              </div>
            </div>

            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using the Decoded platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
              If you disagree with any part of these terms, you may not access the Service.
            </p>

            <h2>2. Description of Service</h2>
            <p>
              Decoded is an AI-powered tariff classification and trade compliance platform that provides:
            </p>
            <ul>
              <li>Harmonized System (HS) code classification suggestions</li>
              <li>Landed cost calculations</li>
              <li>Alternative sourcing analysis</li>
              <li>Trade compliance governance tools</li>
              <li>Tariff refund estimation</li>
            </ul>

            <h2>3. Important Disclaimers</h2>
            <div className="bg-slate-100 rounded-xl p-6 my-6">
              <h3 className="mt-0">Not Legal or Professional Advice</h3>
              <p className="mb-0">
                The classifications, calculations, and recommendations provided by Decoded are for informational purposes only 
                and do not constitute legal, customs, or professional trade advice. You should consult with qualified customs 
                brokers, trade attorneys, or licensed professionals before making any decisions based on our AI-generated suggestions.
              </p>
            </div>
            <p>
              We make no guarantees about the accuracy, completeness, or reliability of AI-generated classifications. 
              Customs authorities make the final determination on HS codes, and our suggestions may differ from official rulings.
            </p>

            <h2>4. User Accounts</h2>
            <p>
              When you create an account, you must provide accurate, complete, and current information. You are responsible for:
            </p>
            <ul>
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>

            <h2>5. Subscription and Payments</h2>
            <p>
              Some features require a paid subscription. By subscribing, you agree to:
            </p>
            <ul>
              <li>Pay all fees associated with your selected plan</li>
              <li>Automatic renewal unless you cancel before the renewal date</li>
              <li>Our refund policy as stated at the time of purchase</li>
            </ul>

            <h2>6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Share account credentials with unauthorized parties</li>
            </ul>

            <h2>7. Intellectual Property</h2>
            <p>
              The Service, including its original content, features, and functionality, is owned by Decoded and is protected 
              by international copyright, trademark, and other intellectual property laws.
            </p>

            <h2>8. Data Privacy</h2>
            <p>
              Your use of the Service is also governed by our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>, 
              which describes how we collect, use, and protect your information.
            </p>

            <h2>9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Decoded shall not be liable for any indirect, incidental, special, 
              consequential, or punitive damages, including but not limited to:
            </p>
            <ul>
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Customs penalties or duties resulting from incorrect classifications</li>
              <li>Delays in shipments or trade operations</li>
              <li>Any decisions made based on AI-generated recommendations</li>
            </ul>

            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Decoded from any claims, damages, or expenses arising from your use 
              of the Service or violation of these Terms.
            </p>

            <h2>11. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email 
              or through the Service. Continued use after changes constitutes acceptance of the new Terms.
            </p>

            <h2>12. Termination</h2>
            <p>
              We may terminate or suspend your account immediately, without prior notice, for any breach of these Terms. 
              Upon termination, your right to use the Service will cease immediately.
            </p>

            <h2>13. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which 
              Decoded operates, without regard to its conflict of law provisions.
            </p>

            <h2>14. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <ul>
              <li>Email: legal@decoded.com</li>
            </ul>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/privacy" className="hover:text-blue-600">Privacy Policy</Link>
          <span className="mx-3">•</span>
          <Link to="/" className="hover:text-blue-600">Return to Decoded</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
